// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0

import { Component, type ErrorInfo, type ReactElement, type ReactNode } from "react";

interface State {
  readonly error: Error | null;
}

interface Props {
  readonly children: ReactNode;
  readonly fallback?: (error: Error, reset: () => void) => ReactNode;
}

// Class component because React's render-phase error catching is only
// available on classes (no hooks equivalent). Without this boundary any
// render exception surfaces as a blank screen with no UI feedback —
// covering the whole tree from ``root.tsx`` so a thrown error in a leaf
// route component still shows the user *something*.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the browser console for now — wire to a real telemetry
    // sink (Sentry, OTel, etc.) when one is configured for the SPA.
    // biome-ignore lint/suspicious/noConsole: error-boundary diagnostic
    console.error("uwhois ErrorBoundary caught:", error, info);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="mx-auto max-w-xl space-y-3 p-8 text-sm">
        <h1 className="font-semibold text-base">Something went wrong.</h1>
        <p className="text-muted-foreground">
          The page raised an unexpected error. Try again, and if it keeps happening, check the
          browser console for details.
        </p>
        <pre className="overflow-auto rounded border border-border bg-muted/40 p-3 text-xs">
          {error.message}
        </pre>
        <button
          type="button"
          onClick={this.reset}
          className="rounded border border-border px-3 py-1 text-xs hover:bg-muted"
        >
          Reset
        </button>
      </div>
    );
  }
}

export function ErrorBoundaryFallback(props: { readonly children: ReactNode }): ReactElement {
  return <ErrorBoundary>{props.children}</ErrorBoundary>;
}
